import React from 'react'
import { useQuery } from '@tanstack/react-query'
import appConfigApi, { AppConfiguration } from '../lib/appConfigApi'
import { useFeatureFlags } from './useFeatureFlags'
import { useAppSelector } from '../store/hooks'

// Icon mapping for sidebar items
import {
  DatabaseOutlined,
  ShareAltOutlined,
  HistoryOutlined,
  MessageOutlined,
  ToolOutlined,
  SettingOutlined,
  ExperimentOutlined,
  AppstoreOutlined,
  FileOutlined,
  CommentOutlined,
  ApiOutlined,
  DashboardOutlined,
  CalendarOutlined,
  MailOutlined,
  FormOutlined,
  FileTextOutlined,
} from '@ant-design/icons'

const iconMap: Record<string, any> = {
  DatabaseOutlined,
  ShareAltOutlined,
  HistoryOutlined,
  MessageOutlined,
  ToolOutlined,
  SettingOutlined,
  ExperimentOutlined,
  AppstoreOutlined,
  FileOutlined,
  CommentOutlined,
  ApiOutlined,
  DashboardOutlined,
  CalendarOutlined,
  MailOutlined,
  FormOutlined,
  FileTextOutlined,
}

export interface SidebarMenuItem {
  key: string
  icon: React.ReactNode
  label: string
  disabled?: boolean
  route?: string
  type?: 'item' | 'group' | 'divider'
}

export const useSidebarConfig = (application: string = 'app') => {
  const { isFeatureEnabled } = useFeatureFlags()
  const { currentProject } = useAppSelector((state) => state.project)

  const { data: sidebarConfig, isLoading, error, refetch } = useQuery({
    queryKey: ['sidebar-configuration', application],
    queryFn: async () => {
      try {
        const config = await appConfigApi.getSidebarConfiguration(application)
        console.log('Sidebar config loaded for', application, ':', config)
        return config
      } catch (err) {
        console.error('Failed to load sidebar config for', application, ':', err)
        throw err
      }
    },
    staleTime: 1 * 60 * 1000, // 1 minute (reduced from 5 minutes for faster updates)
    refetchOnWindowFocus: false,
    retry: 2,
  })

  const processConfigurationItem = (config: AppConfiguration): SidebarMenuItem | null => {
    // Check feature flag if specified
    if (config.featureFlag && !isFeatureEnabled(config.featureFlag)) {
      return null
    }

    // Check if configuration is enabled and visible
    if (!config.isEnabled || !config.isVisible) {
      return null
    }

    // Process route - replace placeholders
    let route = config.route || ''
    if (route.includes('{projectId}') && currentProject) {
      route = route.replace('{projectId}', currentProject.id)
    }

    // Check if item requires project and project is not selected
    const requiresProject = config.config?.requiresProject === true
    const disabled = requiresProject && !currentProject

    return {
      key: route || `#${config.key}`,
      icon: config.icon && iconMap[config.icon] ? React.createElement(iconMap[config.icon]) : null,
      label: config.name,
      disabled,
      route,
      type: 'item',
    }
  }

  const projectMenuItems: SidebarMenuItem[] = 
    sidebarConfig?.projectItems
      ?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      ?.map(processConfigurationItem)
      .filter((item): item is SidebarMenuItem => item !== null) || []

  const systemMenuItems: SidebarMenuItem[] = 
    sidebarConfig?.systemItems
      ?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      ?.map(processConfigurationItem)
      .filter((item): item is SidebarMenuItem => item !== null) || []

  // Debug logging
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    console.log('Processed sidebar items:', { 
      projectMenuItems, 
      systemMenuItems,
      rawConfig: sidebarConfig,
      isLoading,
      error 
    })
  }

  return {
    projectMenuItems,
    systemMenuItems,
    isLoading,
    error,
    refetch,
  }
}